import React, { useCallback, useEffect, useRef, useState } from "react";
import { Canvas } from "@react-three/fiber";
import { DoubleSide, Group } from "three";
import { GeneralDimensions } from "../Utils/Dimensions";
import Colors from "../Utils/Colors";

const colors = [Colors.Cyan, Colors.Magenta, Colors.Yellow];
let cubeLatSize = window.innerWidth < 600 ? 30 : 60; // Adjust size based on window width

const materialProps = {
  metalness: 0,
  roughness: 0,
  transmission: 1,
  thickness: cubeLatSize,
  transparent: false,
  side: DoubleSide
};

const CMYCubeBox: React.FC = () => {
  const meshRef = useRef<Group>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [prevMousePosition, setPrevMousePosition] = useState<
    [number, number] | null
  >(null);

  useEffect(() => {
    const interval = setInterval(() => {
      if (!isDragging && meshRef.current) {
        meshRef.current.rotation.x += 0.01;
        meshRef.current.rotation.y += 0.01;
      }
    }, 30);

    return () => clearInterval(interval);
  }, [isDragging]);

  const handleMouseDown = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      setIsDragging(true);
      setPrevMousePosition([event.clientX, event.clientY]);
    },
    []
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setPrevMousePosition(null);
  }, []);

  const handleMouseMove = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (isDragging && prevMousePosition) {
        const [prevX, prevY] = prevMousePosition;
        const deltaX = event.clientX - prevX;
        const deltaY = event.clientY - prevY;

        if (meshRef.current) {
          meshRef.current.rotation.x += deltaY * 0.01;
          meshRef.current.rotation.y += deltaX * 0.01;
        }

        setPrevMousePosition([event.clientX, event.clientY]);
      }
    },
    [isDragging, prevMousePosition]
  );

  return (
    <div
      style={{
        height: GeneralDimensions.FullHeight,
        width: GeneralDimensions.FullWidth,
        backgroundColor: "#FFFFFF"
      }}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onMouseMove={handleMouseMove}
    >
      <Canvas camera={{ position: [0, 0, 100], near: 1, far: 500 }}>
        <group ref={meshRef}>
          <ambientLight intensity={4} />
          <CMYCube />
        </group>
      </Canvas>
    </div>
  );
};

const CMYCube: React.FC = () => {
  const faceProps = [
    {
      position: [0, 0, -cubeLatSize / 2] as [number, number, number],
      rotation: [-Math.PI, 0, 0] as [number, number, number],
      color: colors[0]
    },
    {
      position: [0, 0, cubeLatSize / 2] as [number, number, number],
      rotation: [Math.PI, Math.PI, 0] as [number, number, number],
      color: colors[0]
    },
    {
      position: [0, -cubeLatSize / 2, 0] as [number, number, number],
      rotation: [Math.PI / 2, 0, 0] as [number, number, number],
      color: colors[1]
    },
    {
      position: [0, cubeLatSize / 2, 0] as [number, number, number],
      rotation: [-Math.PI / 2, 0, 0] as [number, number, number],
      color: colors[1]
    },
    {
      position: [-cubeLatSize / 2, 0, 0] as [number, number, number],
      rotation: [-Math.PI / 2, -Math.PI / 2, 0] as [number, number, number],
      color: colors[2]
    },
    {
      position: [cubeLatSize / 2, 0, 0] as [number, number, number],
      rotation: [Math.PI / 2, Math.PI / 2, 0] as [number, number, number],
      color: colors[2]
    }
  ];

  return (
    <>
      {faceProps.map((props, index) => (
        <mesh key={index} position={props.position} rotation={props.rotation}>
          <planeGeometry args={[cubeLatSize, cubeLatSize, cubeLatSize]} />
          <meshPhysicalMaterial color={props.color} {...materialProps} />
        </mesh>
      ))}
    </>
  );
};

export default CMYCubeBox;
